import { atom, selector } from 'recoil';
import { Account, Invoice } from '@givz/core-client';
import { ChoiceListProps } from '@shopify/polaris';
import {
  AccountOnboardingStep,
  FormattedAccount,
  FormattedInvoiceReport,
} from '../../types';

export const accountState = atom<Account | undefined>({
  default: undefined,
  key: 'accountState',
});

export const invoicesState = atom<Invoice[]>({
  default: [],
  key: 'invoicesState',
});

export const formattedAccountSelector = selector<FormattedAccount | undefined>({
  get: ({ get }) => {
    const account = get(accountState);

    if (!account) return account;

    const updatedStripeProfile = {
      ...account.stripeProfile,
      additionalBillingContacts:
        account.stripeProfile?.additionalBillingContacts?.join(', ') ?? '',
      email: account.stripeProfile?.email ?? '',
    };

    return {
      ...account,
      stripeProfile: updatedStripeProfile,
    } as FormattedAccount;
  },
  key: 'formattedAccountSelector',
});

export const formattedInvoicesReportSelector = selector<
  FormattedInvoiceReport[]
>({
  get: ({ get }) => {
    const invoicesReport = get(invoicesState);
    let formattedInvoicesReport: FormattedInvoiceReport[] = [];

    if (invoicesReport) {
      const currencyFormatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });

      formattedInvoicesReport = invoicesReport.map((invoice) => {
        let dueAt: string | undefined = undefined,
          issuedAt: string | undefined = undefined;
        if (invoice.issuedAt) {
          issuedAt = new Date(invoice.issuedAt).toLocaleDateString();
        }
        if (invoice.dueAt) {
          dueAt = new Date(invoice.dueAt).toLocaleDateString();
        }
        const status =
          invoice.status[0] + invoice.status.slice(1).toLowerCase();
        const total = currencyFormatter.format(invoice.total);

        return {
          ...invoice,
          dueAt,
          issuedAt,
          status,
          total,
        };
      });
    }

    return formattedInvoicesReport;
  },
  key: 'formattedInvoicesReportSelector',
});

export const invoicesFilterChoicesSelector = selector<
  ChoiceListProps['choices']
>({
  get: ({ get }) => {
    const invoices = get(invoicesState);

    return invoices.map((invoice) => {
      return {
        label: invoice.name,
        value: invoice.key,
      };
    });
  },
  key: 'invoicesFilterChoicesSelector',
});

export const showOnboardingFlowState = atom<boolean | undefined>({
  default: undefined,
  key: 'showOnboardingFlowState',
});

export const showOnboardingFlowSelector = selector<boolean>({
  get: ({ get }) => {
    const account = get(accountState);
    const showOnboardingFlow = get(showOnboardingFlowState);

    if (showOnboardingFlow === undefined) {
      return Boolean(!account?.onboarding?.completed);
    }

    return showOnboardingFlow;
  },
  key: 'showOnboardingFlowSelector',
});

export const currentOnboardingStepSelector = selector<
  AccountOnboardingStep | undefined
>({
  get: ({ get }) => {
    const account = get(accountState);

    if (!account) return;

    if (!account?.onboarding?.goal) {
      return AccountOnboardingStep.GOAL;
    }

    if (!account?.onboarding.charitableCauses) {
      return AccountOnboardingStep.CHARITABLE_CAUSES;
    }

    if (!account?.onboarding.referral) {
      return AccountOnboardingStep.REFERRAL;
    }
  },
  key: 'currentOnboardingStepSelector',
});
